import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building, Truck, DollarSign, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { EarningsAPI, PendingPayout, PendingPayoutsResponse } from '../../lib/api';
import { formatCurrency } from '../../lib/formatters';
import { useNotifications } from '../../contexts/NotificationContext';

export default function PendingPayoutsPage() {
  const [searchParams] = useSearchParams();
  const { addNotification } = useNotifications();
  const [loading, setLoading] = useState(true);
  const [payouts, setPayouts] = useState<PendingPayoutsResponse | null>(null);
  const [entityType, setEntityType] = useState<'supplier' | 'transporter' | 'all'>(
    (searchParams.get('type') as any) || 'all'
  );
  const [page, setPage] = useState(1);
  const [selectedPayouts, setSelectedPayouts] = useState<Set<string>>(new Set());
  const [processingPayout, setProcessingPayout] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutForm, setPayoutForm] = useState({
    paymentMethod: 'bank_transfer' as 'bank_transfer' | 'upi' | 'cash' | 'cheque' | 'wallet' | 'neft' | 'rtgs' | 'imps',
    transactionId: '',
    notes: '',
    bankReference: '',
    ifscCode: '',
    region: ''
  });

  useEffect(() => {
    fetchPayouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, page]);

  const fetchPayouts = async () => {
    try {
      setLoading(true);
      const params: any = { page, limit: 20 };
      if (entityType !== 'all') {
        params.entityType = entityType;
      }
      const response = await EarningsAPI.getPendingPayouts(params);
      if (response.success) {
        setPayouts(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching pending payouts:', err);
      addNotification({
        title: 'Error',
        message: err.message || 'Failed to load pending payouts',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPayout = (payoutId: string) => {
    const newSelected = new Set(selectedPayouts);
    if (newSelected.has(payoutId)) {
      newSelected.delete(payoutId);
    } else {
      newSelected.add(payoutId);
    }
    setSelectedPayouts(newSelected);
  };

  const handleSelectAllForEntity = (entityId: string) => {
    const entityPayouts = payouts?.payouts.filter(p => p.entityId === entityId) || [];
    const allSelected = entityPayouts.every(p => selectedPayouts.has(p._id));
    
    const newSelected = new Set(selectedPayouts);
    if (allSelected) {
      entityPayouts.forEach(p => newSelected.delete(p._id));
    } else {
      entityPayouts.forEach(p => newSelected.add(p._id));
    }
    setSelectedPayouts(newSelected);
  };

  const handleProcessPayout = async () => {
    if (selectedPayouts.size === 0) {
      addNotification({
        title: 'Error',
        message: 'Please select at least one payout to process',
        type: 'error'
      });
      return;
    }

    if (!payoutForm.transactionId.trim()) {
      addNotification({
        title: 'Error',
        message: 'Please enter a transaction ID',
        type: 'error'
      });
      return;
    }

    try {
      setProcessingPayout(true);
      
      // Group payouts by entity
      const payoutsByEntity = new Map<string, { entityType: 'supplier' | 'transporter'; payoutIds: string[] }>();
      
      payouts?.payouts.forEach(payout => {
        if (selectedPayouts.has(payout._id)) {
          if (!payoutsByEntity.has(payout.entityId)) {
            payoutsByEntity.set(payout.entityId, {
              entityType: payout.entityType,
              payoutIds: []
            });
          }
          payoutsByEntity.get(payout.entityId)!.payoutIds.push(payout._id);
        }
      });

      // Process each entity's payouts
      const entities = Array.from(payoutsByEntity.entries());
      for (let i = 0; i < entities.length; i++) {
        const [entityId, data] = entities[i];
        await EarningsAPI.processPayout({
          entityId,
          entityType: data.entityType,
          earningIds: data.payoutIds,
          paymentMethod: payoutForm.paymentMethod,
          transactionId: payoutForm.transactionId,
          notes: payoutForm.notes,
          bankReference: payoutForm.bankReference,
          ifscCode: payoutForm.ifscCode,
          region: payoutForm.region
        });
      }

      addNotification({
        title: 'Success',
        message: `Successfully processed ${selectedPayouts.size} payout(s)`,
        type: 'success'
      });
      setShowPayoutModal(false);
      setSelectedPayouts(new Set());
      setPayoutForm({
        paymentMethod: 'bank_transfer',
        transactionId: '',
        notes: '',
        bankReference: '',
        ifscCode: '',
        region: ''
      });
      fetchPayouts();
    } catch (err: any) {
      console.error('Error processing payout:', err);
      addNotification({
        title: 'Error',
        message: err.message || 'Failed to process payout',
        type: 'error'
      });
    } finally {
      setProcessingPayout(false);
    }
  };

  const getSelectedAmount = () => {
    if (!payouts) return 0;
    return payouts.payouts
      .filter(p => selectedPayouts.has(p._id))
      .reduce((sum, p) => sum + p.amount, 0);
  };

  // Group payouts by entity
  const groupedPayouts = payouts?.payouts.reduce((acc, payout) => {
    if (!acc[payout.entityId]) {
      acc[payout.entityId] = {
        entity: payout,
        payouts: []
      };
    }
    acc[payout.entityId].payouts.push(payout);
    return acc;
  }, {} as Record<string, { entity: PendingPayout; payouts: PendingPayout[] }>);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading pending payouts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Pending Payouts</h1>
          <p className="text-muted-foreground">Process payments to suppliers and transporters</p>
        </div>
        <div className="flex gap-2">
          {selectedPayouts.size > 0 && (
            <Button onClick={() => setShowPayoutModal(true)}>
              Process {selectedPayouts.size} Payout(s) - {formatCurrency(getSelectedAmount())}
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Supplier Payouts</p>
                <p className="text-2xl font-bold">{formatCurrency(payouts?.summary?.supplier?.totalPendingAmount || 0)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {payouts?.summary?.supplier?.totalTransactions || 0} transactions
                </p>
              </div>
              <Building className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Transporter Payouts</p>
                <p className="text-2xl font-bold">{formatCurrency(payouts?.summary?.transporter?.totalPendingAmount || 0)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {payouts?.summary?.transporter?.totalTransactions || 0} transactions
                </p>
              </div>
              <Truck className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Pending</p>
                <p className="text-2xl font-bold text-orange-600">
                  {formatCurrency(
                    (payouts?.summary?.supplier?.totalPendingAmount || 0) +
                    (payouts?.summary?.transporter?.totalPendingAmount || 0)
                  )}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {(payouts?.summary?.supplier?.totalTransactions || 0) +
                   (payouts?.summary?.transporter?.totalTransactions || 0)} total
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <Button
          variant={entityType === 'all' ? 'default' : 'outline'}
          onClick={() => setEntityType('all')}
        >
          All
        </Button>
        <Button
          variant={entityType === 'supplier' ? 'default' : 'outline'}
          onClick={() => setEntityType('supplier')}
        >
          Suppliers
        </Button>
        <Button
          variant={entityType === 'transporter' ? 'default' : 'outline'}
          onClick={() => setEntityType('transporter')}
        >
          Transporters
        </Button>
      </div>

      {/* Payouts List */}
      <div className="space-y-4">
        {groupedPayouts && Object.values(groupedPayouts).map(({ entity, payouts: entityPayouts }) => {
          const totalAmount = entityPayouts.reduce((sum, p) => sum + p.amount, 0);
          const allSelected = entityPayouts.every(p => selectedPayouts.has(p._id));
          
          return (
            <Card key={entity.entityId}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => handleSelectAllForEntity(entity.entityId)}
                      className="h-5 w-5 rounded border-gray-300"
                    />
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {entity.entityType === 'supplier' ? (
                          <Building className="h-5 w-5" />
                        ) : (
                          <Truck className="h-5 w-5" />
                        )}
                        {entity.entityName}
                      </CardTitle>
                      <CardDescription>
                        {entity.entityEmail} • {entity.entityMobile}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">{formatCurrency(totalAmount)}</p>
                    <p className="text-sm text-muted-foreground">{entityPayouts.length} transaction(s)</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {entityPayouts.map((payout) => (
                    <div
                      key={payout._id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={selectedPayouts.has(payout._id)}
                          onChange={() => handleSelectPayout(payout._id)}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                        <div>
                          <p className="font-medium">{payout.orderNumber}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(payout.earnedAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <p className="font-semibold">{formatCurrency(payout.amount)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}

        {(!groupedPayouts || Object.keys(groupedPayouts).length === 0) && (
          <Card>
            <CardContent className="p-12 text-center">
              <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <p className="text-lg font-semibold">No Pending Payouts</p>
              <p className="text-muted-foreground">All payments have been processed</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Pagination */}
      {payouts && payouts.pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            disabled={!payouts.pagination.hasPrevPage}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <span className="px-4 py-2">
            Page {payouts.pagination.currentPage} of {payouts.pagination.totalPages}
          </span>
          <Button
            variant="outline"
            disabled={!payouts.pagination.hasNextPage}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Process Payout Modal */}
      <Dialog open={showPayoutModal} onOpenChange={setShowPayoutModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Process Payout</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-2">Selected Payouts</p>
              <p className="text-2xl font-bold">{selectedPayouts.size} transaction(s)</p>
              <p className="text-lg font-semibold text-green-600">{formatCurrency(getSelectedAmount())}</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Payment Method *</label>
              <select
                value={payoutForm.paymentMethod}
                onChange={(e) => setPayoutForm({ ...payoutForm, paymentMethod: e.target.value as any })}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="bank_transfer">Bank Transfer</option>
                <option value="neft">NEFT</option>
                <option value="rtgs">RTGS</option>
                <option value="imps">IMPS</option>
                <option value="upi">UPI</option>
                <option value="cash">Cash</option>
                <option value="cheque">Cheque</option>
                <option value="wallet">Digital Wallet</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Transaction ID *</label>
                <input
                  type="text"
                  value={payoutForm.transactionId}
                  onChange={(e) => setPayoutForm({ ...payoutForm, transactionId: e.target.value })}
                  placeholder="Enter transaction/reference ID"
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Bank Reference (Optional)</label>
                <input
                  type="text"
                  value={payoutForm.bankReference || ''}
                  onChange={(e) => setPayoutForm({ ...payoutForm, bankReference: e.target.value })}
                  placeholder="Bank reference number"
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">IFSC Code (Optional)</label>
                <input
                  type="text"
                  value={payoutForm.ifscCode || ''}
                  onChange={(e) => setPayoutForm({ ...payoutForm, ifscCode: e.target.value.toUpperCase() })}
                  placeholder="IFSC Code"
                  className="w-full px-3 py-2 border rounded-md uppercase"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Region (Optional)</label>
                <input
                  type="text"
                  value={payoutForm.region || ''}
                  onChange={(e) => setPayoutForm({ ...payoutForm, region: e.target.value })}
                  placeholder="Region"
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Notes (Optional)</label>
              <textarea
                value={payoutForm.notes}
                onChange={(e) => setPayoutForm({ ...payoutForm, notes: e.target.value })}
                placeholder="Add any additional notes..."
                rows={3}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayoutModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleProcessPayout} disabled={processingPayout}>
              {processingPayout ? 'Processing...' : 'Process Payout'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
